"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import { Icon } from "@/components/landing/Navbar";
import { motion, AnimatePresence } from "motion/react";
import { Server } from "lucide-react";

const GlowNode = forwardRef<
  HTMLDivElement,
  {
    className?: string;
    children?: React.ReactNode;
    onClick?: () => void;
    size?: "sm" | "md" | "lg";
    glowColor?: string;
    label?: string;
    pulse?: boolean;
    compact?: boolean;
  }
>(
  (
    {
      className,
      children,
      onClick,
      size = "sm",
      glowColor,
      label,
      pulse = false,
      compact = false
    },
    ref
  ) => {
    const nodeSize =
      size === "lg" ? "size-16" : size === "md" ? "size-14" : compact ? "size-10" : "size-12";
    const margin = compact ? "m-1" : "m-4";

    return (
      <div className={cn("flex flex-col items-center gap-2", margin)}>
        <div className="relative">
          <motion.div
            className={cn(
              "absolute inset-[-4px] rounded-full opacity-40",
              glowColor ?? "bg-primary"
            )}
            style={{ filter: "blur(8px)" }}
            animate={
              pulse ? { opacity: [0.2, 0.5, 0.2], scale: [0.95, 1.05, 0.95] } : undefined
            }
            transition={
              pulse ? { duration: 3, repeat: Infinity, ease: "easeInOut" } : undefined
            }
          />
          <motion.div
            className={cn(
              "absolute inset-[-2px] rounded-full opacity-20",
              glowColor ?? "bg-primary"
            )}
            animate={pulse ? { opacity: [0.1, 0.3, 0.1] } : undefined}
            transition={
              pulse
                ? { duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
                : undefined
            }
          />
          <div
            ref={ref}
            onClick={onClick}
            className={cn(
              "relative z-10 flex items-center justify-center rounded-full border select-none border-border/50 bg-card shadow-lg backdrop-blur-sm",
              nodeSize,
              className
            )}
          >
            {children}
          </div>
        </div>
        {label && (
          <span className="text-xs text-muted-foreground tracking-wider select-none text-center whitespace-nowrap">
            {label}
          </span>
        )}
      </div>
    );
  }
);

GlowNode.displayName = "GlowNode";

const OrbitRing = ({ size = 80 }: { size?: number }) => {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        width: size,
        height: size,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -66%)"
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-full border border-primary"
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          className="absolute w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_6px_2px_var(--color-primary)]"
          style={{ top: -3, left: "50%", transform: "translateX(-50%)" }}
        />
      </motion.div>

      <motion.div
        className="absolute inset-[-6px] rounded-full border border-primary/5"
        animate={{ rotate: -360 }}
        transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          className="absolute w-1 h-1 rounded-full bg-primary shadow-[0_0_4px_1px_var(--color-primary)]"
          style={{ top: -2.5, left: "50%", transform: "translateX(-50%)" }}
        />
      </motion.div>
    </div>
  );
};

export const HeroIcons = ({ className, logo }: { className?: string; logo?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef1 = useRef<HTMLDivElement>(null);
  const appRef2 = useRef<HTMLDivElement>(null);
  const appRef3 = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const infraRef = useRef<HTMLDivElement>(null);

  const logos = [
    "/assets/app/nextjs.png",
    "/assets/app/docker.png",
    "/assets/app/java.svg",
    "/assets/app/react.png",
    "/assets/app/php.svg"
  ];

  const [logoIndices, setLogoIndices] = useState<[number, number, number]>([0, 2, 4]);
  const [transitioning, setTransitioning] = useState<[boolean, boolean, boolean]>([
    false,
    false,
    false
  ]);

  useEffect(() => {
    if (logo) {
      return;
    }

    const intervalIds = ([0, 1, 2] as const).map(i => {
      return setInterval(
        () => {
          setTransitioning(prev => {
            const next: [boolean, boolean, boolean] = [...prev] as [boolean, boolean, boolean];
            next[i] = true;
            return next;
          });
          setTimeout(() => {
            setLogoIndices(prev => {
              const next: [number, number, number] = [...prev] as [number, number, number];
              next[i] = (next[i] + 1) % logos.length;
              return next;
            });
            setTransitioning(prev => {
              const next: [boolean, boolean, boolean] = [...prev] as [
                boolean,
                boolean,
                boolean
              ];
              next[i] = false;
              return next;
            });
          }, 150);
        },
        3000 + i * 900
      );
    });

    return () => intervalIds.forEach(clearInterval);
  }, [logo, logos.length]);

  const appRefs = [appRef1, appRef2, appRef3] as const;

  return (
    <div
      className={cn(
        "relative flex h-[210px] w-full items-center justify-center overflow-hidden",
        className
      )}
      ref={containerRef}
    >
      <div className="flex size-full max-w-2xl flex-row items-center justify-between gap-4 ml-2">
        {/* Multiple App Nodes */}
        <div className="flex flex-col items-center gap-2">
          {appRefs.map((ref, i) => (
            <GlowNode
              key={i}
              ref={ref}
              compact
              className={transitioning[i] ? "opacity-60" : "opacity-100"}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={logos[logoIndices[i]]}
                  src={logos[logoIndices[i]]}
                  alt="App"
                  width={24}
                  height={24}
                  className="h-5 w-5 rounded-full"
                  initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.6, rotate: 30 }}
                  transition={{ duration: 0.25 }}
                />
              </AnimatePresence>
            </GlowNode>
          ))}
          <span className="text-xs text-muted-foreground tracking-wider select-none mt-1 whitespace-nowrap">
            Your Apps
          </span>
        </div>

        {/* DollarDeploy Node - Center */}
        <div className="relative ml-6">
          <OrbitRing size={76} />
          <GlowNode size="sm" glowColor="bg-primary" ref={centerRef} label="&nbsp;" pulse>
            <Link
              href="/dashboard"
              className="group flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-full text-lg font-semibold text-primary-foreground"
              title="DollarDeploy"
            >
              <Icon className="h-8 w-8 mt-0.5 mr-0.5 transition-all group-hover:scale-110" />
            </Link>
          </GlowNode>
        </div>

        {/* Infrastructure Node */}
        <div className="relative">
          <GlowNode ref={infraRef} label="Your Infra" size="md">
            <Server className="h-6 w-6 text-foreground" />
          </GlowNode>
        </div>
      </div>

      <div>
        {/* Beams from each app to center */}
        {/* eslint-disable-next-line react-hooks/refs */}
        {appRefs.map((ref, i) => (
          <AnimatedBeam
            key={`fwd-${i}`}
            duration={3}
            pathWidth={3}
            pathOpacity={0.1}
            straight
            containerRef={containerRef}
            fromRef={ref}
            toRef={centerRef}
            gradientStartColor="#ffaa40"
            gradientStopColor="#9c40ff"
          />
        ))}

        {/* Beam from center to infrastructure */}
        <AnimatedBeam
          duration={3}
          pathWidth={3}
          pathOpacity={0.1}
          straight
          containerRef={containerRef}
          fromRef={centerRef}
          toRef={infraRef}
          gradientStartColor="#9c40ff"
          gradientStopColor="#40c9ff"
        />

        {/* Reverse beams for bidirectional flow effect */}
        {/* eslint-disable-next-line react-hooks/refs */}
        {appRefs.map((ref, i) => (
          <AnimatedBeam
            key={`rev-${i}`}
            duration={4}
            pathWidth={2}
            pathOpacity={0}
            straight
            containerRef={containerRef}
            fromRef={ref}
            toRef={centerRef}
            gradientStartColor="#40c9ff"
            gradientStopColor="#ffaa40"
            reverse
            delay={1.5}
          />
        ))}

        <AnimatedBeam
          duration={4}
          pathWidth={2}
          pathOpacity={0}
          straight
          containerRef={containerRef}
          fromRef={centerRef}
          toRef={infraRef}
          gradientStartColor="#ffaa40"
          gradientStopColor="#40c9ff"
          reverse
          delay={1.5}
        />
      </div>
    </div>
  );
};

export default HeroIcons;
